import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { Icon } from './ui';
import { scrollflaeche } from '../utils/scrollflaeche';

/**
 * „Zurück“, das dorthin führt, woher man kam – und das Ziel beim Namen nennt („Zurück zum Kunden“).
 *
 * React Router legt die Position im Browser-Verlauf in `history.state.idx` ab. Das Layout merkt sich
 * mit [useVerlaufMerken] für jede Position die Adresse (in der Session, übersteht also ein Neuladen);
 * der Link schaut nach, was eine Position davor lag, und geht mit `navigate(-1)` genau dorthin – so
 * bleiben auch Filter und Scrollstand der vorigen Seite erhalten. Kam man direkt über eine Adresse
 * (neuer Tab, Lesezeichen), gibt es kein „davor“: dann gilt das feste `fallback`.
 */

const SCHLUESSEL = 'hp.verlauf';

const lesen = (): Record<string, string> => {
  try { return JSON.parse(sessionStorage.getItem(SCHLUESSEL) || '{}'); } catch { return {}; }
};

const position = (): number | undefined => (window.history.state as { idx?: number } | null)?.idx;

/** Im Layout aufrufen: merkt sich die Adresse jeder Verlaufsposition. */
export const useVerlaufMerken = () => {
  const location = useLocation();
  useEffect(() => {
    const idx = position();
    if (idx === undefined) return;
    const verlauf = lesen();
    verlauf[idx] = location.pathname + location.search;
    try { sessionStorage.setItem(SCHLUESSEL, JSON.stringify(verlauf)); } catch { /* dann eben ohne */ }
  }, [location]);
};

const SCROLL_SCHLUESSEL = 'hp.scroll';

const scrollLesen = (): Record<string, number> => {
  try { return JSON.parse(sessionStorage.getItem(SCROLL_SCHLUESSEL) || '{}'); } catch { return {}; }
};

/**
 * Scrollstand je Verlaufsposition: neue Seiten beginnen oben, beim Zurückgehen (App-„Zurück“ oder
 * Browser) steht man wieder dort, wo man war – in einer langen Liste also beim zuletzt geöffneten
 * Eintrag. Weil die Liste erst geladen und stückweise gerendert wird, wird ein paar Mal versucht,
 * bis die Seite hoch genug ist; jede eigene Bewegung des Nutzers bricht das ab.
 */
export const useScrollstandMerken = () => {
  const location = useLocation();
  const art = useNavigationType();

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    const flaeche = scrollflaeche();
    if (!flaeche) return;
    const idx = position();
    let rahmen = 0;
    const merken = () => {
      cancelAnimationFrame(rahmen);
      rahmen = requestAnimationFrame(() => {
        if (idx === undefined) return;
        const stand = scrollLesen();
        stand[idx] = flaeche.scrollTop;
        try { sessionStorage.setItem(SCROLL_SCHLUESSEL, JSON.stringify(stand)); } catch { /* egal */ }
      });
    };
    flaeche.addEventListener('scroll', merken, { passive: true });

    const ziel = (art === 'POP' && idx !== undefined ? scrollLesen()[idx] : 0) ?? 0;
    let versuche = 0;
    let timer = 0;
    const abbrechen = () => window.clearTimeout(timer);
    const versuchen = () => {
      flaeche.scrollTop = ziel;
      if (Math.abs(flaeche.scrollTop - ziel) > 2 && versuche++ < 30) timer = window.setTimeout(versuchen, 60);
    };
    versuchen();
    flaeche.addEventListener('wheel', abbrechen, { passive: true, once: true });
    flaeche.addEventListener('touchstart', abbrechen, { passive: true, once: true });

    return () => {
      flaeche.removeEventListener('scroll', merken);
      flaeche.removeEventListener('wheel', abbrechen);
      flaeche.removeEventListener('touchstart', abbrechen);
      abbrechen();
      cancelAnimationFrame(rahmen);
    };
  }, [location.key, art]);
};

// Wie die Seite im Satz heißt – „Zurück <…>“. Unbekannte Seiten bekommen kein Ziel im Namen.
const ZIELE: [RegExp, string][] = [
  [/^\/dashboard/, 'zum Dashboard'],
  [/^\/kunden\/\d+/, 'zum Kunden'],
  [/^\/kunden/, 'zu den Kunden'],
  [/^\/karte/, 'zur Karte'],
  [/^\/potenziale/, 'zu den Potenzialen'],
  [/^\/beratungstermine/, 'zu den Beratungsterminen'],
  [/^\/berichte/, 'zu den Berichten'],
  [/^\/protokolle/, 'zu den Protokollen'],
  [/^\/waren/, 'zu den Artikeln'],
  [/^\/bauteile/, 'zu den Elektrobauteilen'],
  [/^\/aufgaben/, 'zu den Aufgaben'],
  [/^\/arbeitszeiten/, 'zu den Arbeitszeiten'],
  [/^\/zeiterfassung/, 'zur Zeiterfassung'],
];

const zielName = (pfad: string) => ZIELE.find(([muster]) => muster.test(pfad))?.[1];

const KLASSE = 'inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-900';

export const ZurueckLink: React.FC<{ fallback: string; fallbackLabel: string }> = ({ fallback, fallbackLabel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const idx = position();
  const vorher = idx !== undefined && idx > 0 ? lesen()[idx - 1] : undefined;
  const name = vorher && vorher !== location.pathname + location.search ? zielName(vorher) : undefined;

  if (!name) {
    return <Link to={fallback} className={KLASSE}><Icon.ArrowLeft className="h-4 w-4" /> {fallbackLabel}</Link>;
  }
  return (
    <button type="button" onClick={() => navigate(-1)} className={KLASSE}>
      <Icon.ArrowLeft className="h-4 w-4" /> Zurück {name}
    </button>
  );
};
